import React, { useEffect, useState } from "react";
import { apiGet, apiPost } from "../../api";

const GROUPS = [
  { title: "Income", slots: ["income_corporate", "income_personal"] },
  { title: "Disbursements & discounts", slots: [
      "disbursements_corporate", "disbursements_personal",
      "discount_corporate", "discount_personal"] },
  { title: "Tax", slots: ["tax_NONE", "tax_GST", "tax_GST_PST", "tax_HST"] },
];

export default function AccountMappingTab() {
  const [slots, setSlots] = useState(null);
  const [refs, setRefs] = useState(null);
  const [choice, setChoice] = useState({});     // slot -> qbo ref id
  const [roles, setRoles] = useState({});       // slot -> { component: rate ref }
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      apiGet("/api/quickbooks/mapping/slots/"),
      apiGet("/api/quickbooks/mapping/refs/"),
    ])
      .then(([s, r]) => {
        setSlots(s.slots);
        setRefs(r);
        const initial = {};
        const initialRoles = {};
        s.slots.forEach((row) => {
          initial[row.slot] = row.qbo_ref_id || "";
          initialRoles[row.slot] = { ...(row.rate_roles || {}) };
        });
        setChoice(initial);
        setRoles(initialRoles);
      })
      .catch((err) => {
        const detail = err && err.response && err.response.data && err.response.data.detail;
        setError(detail || "Could not reach QuickBooks. Check the connection under Settings.");
      });
  }, []);

  if (error) return <div style={{ padding: 24 }}>{error}</div>;
  if (!slots || !refs) return <div>Loading…</div>;

  const optionsFor = (refType) => {
    if (refType === "item") return (refs.items || []).map((i) => ({ id: String(i.Id), name: i.FullyQualifiedName || i.Name }));
    if (refType === "account") return (refs.accounts || []).map((a) => ({ id: String(a.Id), name: a.FullyQualifiedName || a.Name }));
    return (refs.tax_codes || []).map((t) => ({ id: String(t.Id), name: t.Name }));
  };

  // The rates the currently-chosen tax code carries, which is what a role
  // dropdown offers. Empty until a code is picked.
  const ratesFor = (row) => {
    if (row.ref_type !== "taxcode") return [];
    const code = (refs.tax_codes || []).find((t) => String(t.Id) === choice[row.slot]);
    return (code && code.rate_refs) || [];
  };

  // A tax slot is not mapped until every component it needs has a rate. A code
  // with no rate assigned would build a payload that fails at push time, so it
  // counts as incomplete here for the same reason the backend counts it missing.
  const isComplete = (row) => {
    if (!choice[row.slot]) return false;
    return (row.required_roles || []).every((r) => (roles[row.slot] || {})[r]);
  };

  const unmapped = slots.filter((s) => !isComplete(s));

  // GROUPS controls display order and grouping only, never which slots exist.
  // Any slot the API returns that isn't listed in a group still needs a
  // dropdown, so it falls into a trailing "Other" group instead of vanishing.
  const groupedSlotNames = new Set(GROUPS.reduce((acc, g) => acc.concat(g.slots), []));
  const otherSlots = slots.filter((s) => !groupedSlotNames.has(s.slot));
  const renderGroups = otherSlots.length > 0
    ? [...GROUPS, { title: "Other", slots: otherSlots.map((s) => s.slot) }]
    : GROUPS;

  const save = async () => {
    const payload = slots.map((row) => {
      const id = choice[row.slot] || "";
      const opt = optionsFor(row.ref_type).find((o) => o.id === id);
      const taxCode = (refs.tax_codes || []).find((t) => String(t.Id) === id);
      return {
        slot: row.slot,
        qbo_ref_id: id,
        qbo_display_name: opt ? opt.name : "",
        rate_refs: row.ref_type === "taxcode" && taxCode ? taxCode.rate_refs || [] : [],
        rate_roles: (row.required_roles || []).length ? (roles[row.slot] || {}) : {},
      };
    });
    const res = await apiPost("/api/quickbooks/mapping/slots/", { slots: payload });
    setMsg(`Saved. ${res.missing.length} slot(s) still unmapped.`);
  };

  return (
    <div>
      {unmapped.length > 0 && (
        <p style={{ color: "#92400e", fontWeight: 600 }}>
          {unmapped.length} of {slots.length} slots are not mapped. Invoices using an
          unmapped slot will not push.
        </p>
      )}
      {renderGroups.map((g) => (
        <div key={g.title} style={{ marginBottom: 20 }}>
          <h3>{g.title}</h3>
          {slots.filter((s) => g.slots.indexOf(s.slot) !== -1).map((row) => (
            <div key={row.slot} style={{ marginBottom: 6 }}>
              <label htmlFor={row.slot} style={{ display: "inline-block", width: 240 }}>
                {row.slot}
              </label>
              <select
                id={row.slot}
                aria-label={row.slot}
                value={choice[row.slot] || ""}
                onChange={(e) => setChoice((c) => ({ ...c, [row.slot]: e.target.value }))}
              >
                <option value="">— not mapped —</option>
                {optionsFor(row.ref_type).map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              {(row.required_roles || []).length > 0 && choice[row.slot] && (
                <div style={{ marginLeft: 240, marginTop: 4 }}>
                  {(row.required_roles || []).map((component) => (
                    <div key={component} style={{ marginBottom: 4 }}>
                      <label
                        htmlFor={`${row.slot}-role-${component}`}
                        style={{ display: "inline-block", width: 120, fontSize: 13 }}
                      >
                        {component} is
                      </label>
                      <select
                        id={`${row.slot}-role-${component}`}
                        aria-label={`${row.slot} role ${component}`}
                        value={(roles[row.slot] || {})[component] || ""}
                        onChange={(e) =>
                          setRoles((r) => ({
                            ...r,
                            [row.slot]: { ...(r[row.slot] || {}), [component]: e.target.value },
                          }))
                        }
                      >
                        <option value="">— which rate? —</option>
                        {ratesFor(row).map((rate) => (
                          <option key={rate.value} value={rate.value}>
                            {rate.name} ({rate.rate}%)
                          </option>
                        ))}
                      </select>
                      {(() => {
                        // Warn, do not block: a province may legitimately
                        // charge something other than what we bill. But an HST
                        // component pointed at a 5% rate is a filing error, and
                        // this is the moment to catch it -- the amounts sent
                        // are ClientZone's, so QuickBooks would record 13% of
                        // tax against a 5% GST rate and never complain.
                        const chosen = (roles[row.slot] || {})[component];
                        if (!chosen) return null;
                        const rate = ratesFor(row).find((r) => r.value === chosen);
                        const want = (row.expected_percent || {})[component];
                        if (!rate || !want || rate.rate === "") return null;
                        if (Number(rate.rate) === Number(want)) return null;
                        return (
                          <div
                            data-testid={`rate-warning-${row.slot}-${component}`}
                            style={{ color: "#b91c1c", fontSize: 13, marginLeft: 120 }}
                          >
                            ClientZone bills {component} at {want}%, but this rate is{" "}
                            {rate.rate}%. The tax would be recorded against the wrong rate.
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
      <button onClick={save}>Save mapping</button>
      {msg && <span style={{ marginLeft: 12, color: "#065f46" }}>{msg}</span>}
    </div>
  );
}
